import { graphql } from "gatsby"
import React from "react"
import NewLayout from "../components/new_layout"
import SEO from "../components/seo"
import MainSection100 from "../components/Layout/main-section-100"
import MainSection50 from "../components/Layout/main-section-50"
import Tag from "../components/v2023/UI/Tag/tag"
import LinkButton from "../components/v2023/UI/LinkButton/link-button"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import ListHighlight from "../components/v2024/list/list-highlight"
import { SquareTabs } from "../components/v2026/square-tabs"
import ItemGroup from "../components/v2023/UI/Item/item-group"
import Channels from "../components/v2023/CustomerHub/channels"
import { ResponsiveImage } from "../components/v2024/responsive-image"
import Opinions from "../components/v2023/Opinions/opinions"
import ReadyToTalk from "../components/v2023/ReadyToTalk/ready-to-talk"
import ReactMarkdown from "react-markdown"
import rehypeRaw from "rehype-raw"
import MenuModalState from "../components/v2024/Login/modal-login-state"
import { ModalForm } from "../components/v2024/modal"
import { Container } from '../components/Layout/containers';
import ImageButton from "../components/v2023/UI/ImageButton/image-button"

const AntiFraudControlsV2 = ({ data, pageContext }) => {
    const lang = pageContext.langKey
    const page = data.page
    const demoModalState = MenuModalState()

    return (
        <NewLayout pageContext={pageContext}>
            <SEO
                lang={pageContext.langKey}
                title={page.seo.title}
                description={page.seo.meta_description}
                translates={pageContext.translates}
                image={page.seo.image_2.localFile.publicURL}
            />
            {/* HERO SECTION */}
            <MainSection50
                sectionType="color"
                sectionColor="purple-dark"
                sectionSize="lg"
                childrenLeft={
                    <>
                        <Tag tag={{ name: page.tag, type: 'Default' }} />
                        <h1 className="hero--thin">{page.hero_title}</h1>
                        <div className="container__text">
                            <p>{page.hero_description}</p>
                        </div>
                        <button className="button button--ghost button--xl" onClick={() => demoModalState.openMenu()}>
                            {page.hero_button.text}
                        </button>
                    </>
                }
                childrenRight={
                    <GatsbyImage image={getImage(page.hero_image.localFile)} alt={page.hero_image.alternativeText} />
                }
            />
            {/* STEPS SECTION */}
            <MainSection100>
                <div className="grid-lg-4">
                    <ListHighlight vertical items={page.steps} />
                </div>
            </MainSection100>
            {/* FUNCTIONALITIES SECTION */}
            <MainSection100>
                <div className="container__text">
                    <h1>{page.functionalities_title}</h1>
                    <ReactMarkdown children={page.functionalities_description} rehypePlugins={[rehypeRaw]} />
                </div>
                <ItemGroup items={page.functionalities} hasImage unboxed />
            </MainSection100>
            {/* AI FUNCTIONALITIES SECTION */}
            <MainSection100 noContainer>
                <Container>
                    <div className="container__text">
                        <h1>{page.ai_features_title}</h1>
                        <ReactMarkdown children={page.ai_features_description} rehypePlugins={[rehypeRaw]} />
                    </div>
                    <SquareTabs tabs={page.ai_features} />
                </Container>
            </MainSection100>
            {/* OPEN BANKING & OPEN GATEWAY SECTION */}
            <MainSection100>
                <div className="container__text">
                    <h1>{page.banking_title}</h1>
                    {/* <p>{page.banking_description}</p> */}
                    <ReactMarkdown children={page.banking_description} rehypePlugins={[rehypeRaw]} />
                </div>
                <div className="flex-responsive">
                    {page.banking_open.map(item => 
                        <ImageButton button={item} />
                    )}
                </div>
                <div className="tile tile--green">
                    {/* <h3>{page.banking_cta.name}</h3> */}
                    <div className="tile__body">
                        <ReactMarkdown children={page.banking_cta.description} rehypePlugins={[rehypeRaw]} />
                    </div>
                </div>
            </MainSection100>
            {/* COMPLIANCE HUB SECTION */}
            <Channels channels={page.compliance_hub_info} />
            {/* COMPLIANCE SECTION */}
            <MainSection100>
                <div className="container__text">
                    <h1>{page.compliance_title}</h1>
                    <p>{page.compliance_description}</p>
                </div>
                <ResponsiveImage image1440={page.compliance_image} image992={page.compliance_image} image768={page.compliance_image_768} image576={page.compliance_image_576} />
            </MainSection100>
            {/* OPINIONS */}
            <Opinions lang={lang} successCases={true} />
            {/* ADAPTATION SECTION */}
            <MainSection50 sectionSize="sm"
                childrenLeft={
                    <>
                        <h1>{page.adaptation_title}</h1>
                        <ReactMarkdown children={page.adaptation_description} rehypePlugins={[rehypeRaw]} />
                    </>
                }
                childrenRight={
                    <GatsbyImage image={getImage(page.adaptation_image.localFile)} alt={page.adaptation_image.alternativeText} />
                }
            />
            {/* READY TO TALK */}
            <MainSection100>
                <div className="ready-to-talk-simple">
                    <ReadyToTalk lang={lang} hideTags />
                </div>
            </MainSection100>
            {/* REQUEST DEMO MODAL */}
            <ModalForm
                form={page.request_demo_form}
                state={demoModalState}
                divFormId="anti-fraud-form"
            />
        </NewLayout>
    )
}

export default AntiFraudControlsV2

export const antiFraudV2Query = graphql`
    query ($langKey: String) {
        page: strapi2026AntiFraud (locale: { eq: $langKey }) {
            seo {
                title
                meta_description
                image_2 {
					localFile {
						publicURL
					}
				}
            }
            tag
            hero_title
            hero_description
            hero_button {
                text
                url
            }
            hero_image {
                localFile {
                    childImageSharp {
                        gatsbyImageData(
                            width: 656, 
                            placeholder: BLURRED, 
                            formats: [WEBP],
                            quality: 100,
                            breakpoints: [636, 576, 520, 512, 350]
                        )
                    }
                }
                alternativeText
            }
            steps {
                order
                title
                description
            }
            functionalities_title
            functionalities_description
            functionalities {
                id
                order
                title
                description
                icon {
                    name
                    alternativeText
                    localFile {
                        childImageSharp {
                            gatsbyImageData(
                                width: 608, 
                                placeholder: BLURRED, 
                                formats: [WEBP], 
                                breakpoints: [512, 350]
                            )
                        }
                    }
                }
            }
            ai_features_title
            ai_features_description
            ai_features {
                proceso_onboarding_name
                proceso_onboarding_icon
                proceso_onboarding_item {
                    id
                    titulo
                    descripcion
                    imagen {
                        localFile {
                            childImageSharp {
                                gatsbyImageData(
                                    placeholder: BLURRED, 
                                    formats: [WEBP], 
                                    breakpoints: [512, 350]
                                )
                            }
                        }
                    }
                }
            }
            banking_title
            banking_description
            banking_open {
                url
                image {
                    alternativeText
                    localFile {
                        childImageSharp {
                            gatsbyImageData(
                                width: 228, 
                                placeholder: BLURRED, 
                                formats: [WEBP], 
                                breakpoints: [350]
                            )
                        }
                    }
                }
            }
            banking_cta {
                description
            }
            compliance_hub_info {
                tags {
                    name
                    type
                }
                title
                description
                button {
                    text
                    url
                }
                image {
                    alternativeText
                    localFile {
                        childImageSharp {
                            gatsbyImageData (
                                placeholder: BLURRED, 
                                formats: [WEBP],
                                quality: 100,
                                breakpoints: [636, 576, 520, 512, 350]
                            )
                        }
                    }
                }
            }
            compliance_title
            compliance_description
            compliance_image {
                alternativeText
                localFile {
                    childImageSharp {
                        gatsbyImageData (
                            placeholder: BLURRED, 
                            formats: [WEBP],
                            quality: 100,
                            breakpoints: [636, 576, 520, 512, 350]
                        )
                    }
                }
            }
            compliance_image_768 {
                alternativeText
                localFile {
                    childImageSharp {
                        gatsbyImageData (
                            placeholder: BLURRED, 
                            formats: [WEBP],
                            quality: 100,
                            breakpoints: [636, 576, 520, 512, 350]
                        )
                    }
                }
            }
            compliance_image_576 {
                alternativeText
                localFile {
                    childImageSharp {
                        gatsbyImageData (
                            placeholder: BLURRED, 
                            formats: [WEBP],
                            quality: 100,
                            breakpoints: [520, 512, 350]
                        )
                    }
                }
            }
            adaptation_title
            adaptation_description
            adaptation_image {
                alternativeText
                localFile {
                    childImageSharp {
                        gatsbyImageData
                    }
                }
            }
            request_demo_form {
                form_id
                portal_id
                region
                title
                description
            }
        }
    }
`